import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from 'lib/queryClient'
import {
  fetchOfferAttributes,
  LeaseDisclosures,
  OfferAttributes,
  updateOfferAttributes,
} from 'services/offerAttributesApi'

/**
 * Hook to fetch offer attributes
 */
export const useOfferAttributes = (teamGuid: string, offerGuid: string) => {
  return useQuery({
    queryKey: queryKeys.offerAttributes.detail(teamGuid, offerGuid),
    queryFn: () => fetchOfferAttributes(teamGuid, offerGuid),
    enabled: !!teamGuid && !!offerGuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

interface UpdateOfferAttributesVariables {
  teamGuid: string
  offerGuid: string
  leaseDisclosures: Partial<LeaseDisclosures>
}

/**
 * Hook to update offer attributes with optimistic updates
 */
export const useUpdateOfferAttributes = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: UpdateOfferAttributesVariables) =>
      updateOfferAttributes(
        variables.teamGuid,
        variables.offerGuid,
        variables.leaseDisclosures,
      ),

    onMutate: async (variables: UpdateOfferAttributesVariables) => {
      const queryKey = queryKeys.offerAttributes.detail(
        variables.teamGuid,
        variables.offerGuid,
      )

      await queryClient.cancelQueries({ queryKey })

      const previousData = queryClient.getQueryData<OfferAttributes>(queryKey)

      // Optimistic update
      queryClient.setQueryData(
        queryKey,
        (old: OfferAttributes | undefined) => ({
          ...(old || {}),
          lease_disclosures: {
            ...(old?.lease_disclosures || {}),
            ...variables.leaseDisclosures,
          },
        }),
      )

      return { previousData }
    },

    onError: (_err, variables, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(
          queryKeys.offerAttributes.detail(
            variables.teamGuid,
            variables.offerGuid,
          ),
          context.previousData,
        )
      }
    },

    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.offerAttributes.detail(
          variables.teamGuid,
          variables.offerGuid,
        ),
      })
    },

    retry: false,
  })
}
